<template>
  <v-layout>
    <v-flex xs12>
      <h1 v-if="!id" class="headline">New Open Group</h1>
      <h1 v-else class="headline">{{ editLabel }}</h1>

      <v-stepper v-model="step" non-linear>
        <v-stepper-header>
          <v-stepper-step :complete="step > 1" step="1" editable
            >Details</v-stepper-step
          >
          <v-divider></v-divider>
          <v-stepper-step :complete="step > 2" step="2" :editable="isValid"
            >Services</v-stepper-step
          >
          <v-divider></v-divider>
          <v-stepper-step :complete="step > 3" step="3" :editable="isValid"
            >Departure Dates</v-stepper-step
          >
        </v-stepper-header>

        <v-stepper-items>
          <v-stepper-content step="1">
            <v-container fluid grid-list-xs>
              <v-layout row wrap fill-height>
                <v-flex xs12 sm4 class="px-1">
                  <v-text-field
                    v-model="name"
                    label="Name"
                    :outlined="outlinedInputs"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm3 class="px-1">
                  <destination-list-select
                    :destination="destination"
                    :set-default="false"
                    :outlined="outlinedInputs"
                    @onSelect="onDestinationSelect($event)"
                  ></destination-list-select>
                </v-flex>
                <v-flex xs12 sm3 class="px-1">
                  <department-list
                    v-model="department"
                    :outlined="outlinedInputs"
                  ></department-list>
                </v-flex>
                <v-flex xs12 sm2 class="px-1">
                  <v-text-field
                    v-model.number="duration"
                    label="Duration (Days)"
                    type="number"
                    min="1"
                    :outlined="outlinedInputs"
                  ></v-text-field>
                </v-flex>
              </v-layout>
              <v-layout row wrap>
                <v-flex xs12 sm2 class="px-1">
                  <v-text-field
                    v-model.number="adults"
                    label="Adults"
                    type="number"
                    min="0"
                    :outlined="outlinedInputs"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm2 class="px-1">
                  <v-text-field
                    v-model.number="children"
                    label="Children"
                    type="number"
                    min="0"
                    :outlined="outlinedInputs"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm3 class="px-1">
                  <agent-list-select
                    :agent="agent"
                    :outlined="outlinedInputs"
                    @onSelect="onAgentSelect($event)"
                  ></agent-list-select>
                </v-flex>
                <v-flex xs12 sm5 class="px-1">
                  <dates-range-picker
                    :gt-date-from-string="validFrom"
                    :gt-date-to-string="validTo"
                    custom-date-from-label="Valid From"
                    custom-date-to-label="Valid To"
                    :outlined="outlinedInputs"
                    :clearable="true"
                    @onRangeSelected="onRangeSelected($event)"
                    @onClear="
                      validFrom = null;
                      validTo = null;
                    "
                    hide-details
                  ></dates-range-picker>
                </v-flex>
              </v-layout>
              <v-layout row wrap>
                <v-flex xs12 class="px-1">
                  <v-select
                    v-model="departureDays"
                    :items="dayOptions"
                    item-text="name"
                    item-value="value"
                    label="Days of Week"
                    multiple
                    chips
                    deletable-chips
                    :outlined="outlinedInputs"
                  ></v-select>
                </v-flex>
              </v-layout>
              <v-layout>
                <v-flex xs12>
                  <v-textarea
                    v-model="description"
                    label="Short Description"
                    rows="2"
                    :outlined="outlinedInputs"
                  ></v-textarea>
                </v-flex>
              </v-layout>
            </v-container>
            <v-btn color="primary" text :disabled="!isValid" @click="step = 2">
              Continue To Services
              <v-icon right small>mdi-arrow-right</v-icon>
            </v-btn>
          </v-stepper-content>

          <v-stepper-content step="2">
            <v-btn color="primary" text @click="step = 3">
              Continue To Departure Dates
              <v-icon right small>mdi-arrow-right</v-icon>
            </v-btn>
          </v-stepper-content>

          <v-stepper-content step="3"> </v-stepper-content>
        </v-stepper-items>
      </v-stepper>

      <v-layout justify-center pa-3>
        <v-btn color="primary" :disabled="!isValid" @click="onSave">{{
          saveLabel
        }}</v-btn>
      </v-layout>
    </v-flex>
  </v-layout>
</template>

<script>
import gql from "graphql-tag";
import DestinationListSelect from "../shared/DestinationListSelect";
import AgentListSelect from "../shared/AgentListSelect";
import DepartmentList from "../shared/DepartmentListSelect";
import DatesRangePicker from "../shared/DatesRangePicker";

const OPEN_GROUP_QUERY = gql`
  query ($id: ID!) {
    openGroup(id: $id) {
      id
      name
      description
      department
      duration
      adults
      children
      validFrom
      validTo
      departureDays
      destination {
        id
        name
      }
      agent {
        id
        name
      }
    }
  }
`;

const CREATE_OPEN_GROUP = gql`
  mutation ($input: OpenGroupInput!) {
    createOpenGroup(openGroupInput: $input) {
      id
    }
  }
`;

const UPDATE_OPEN_GROUP = gql`
  mutation ($id: ID!, $input: OpenGroupInput!) {
    updateOpenGroup(id: $id, openGroupInput: $input) {
      id
    }
  }
`;

export default {
  name: "OpenGroupBuilder",

  components: {
    DestinationListSelect,
    AgentListSelect,
    DepartmentList,
    DatesRangePicker,
  },

  data() {
    return {
      step: 1,
      name: "",
      description: "",
      department: "",
      duration: null,
      adults: 0,
      children: 0,
      destination: null,
      agent: null,
      validFrom: null,
      validTo: null,
      departureDays: [],
      dayOptions: [
        { name: "Monday", value: "1" },
        { name: "Tuesday", value: "2" },
        { name: "Wednesday", value: "3" },
        { name: "Thursday", value: "4" },
        { name: "Friday", value: "5" },
        { name: "Saturday", value: "6" },
        { name: "Sunday", value: "0" },
      ],
    };
  },

  computed: {
    id() {
      return this.$route.params.id;
    },
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    isValid() {
      return this.name != "";
    },
    editLabel() {
      return `${this.name}${
        this.destination ? " / " + this.destination.name : ""
      }${this.duration ? " / " + this.duration + " days" : ""}`;
    },
    saveLabel() {
      return this.id ? "Update Open Group" : "Create Open Group";
    },
  },

  methods: {
    onDestinationSelect(dest) {
      this.destination = dest;
    },
    onAgentSelect(agent) {
      this.agent = agent;
    },
    onRangeSelected({ dateFrom, dateTo }) {
      this.validFrom = dateFrom;
      this.validTo = dateTo;
    },
    buildInput() {
      return {
        name: this.name,
        description: this.description,
        department: this.department,
        duration: this.duration,
        adults: this.adults,
        children: this.children,
        destinationId: this.destination ? this.destination.id : null,
        agentId: this.agent ? this.agent.id : null,
        validFrom: this.validFrom,
        validTo: this.validTo,
        departureDays: this.departureDays.join(","),
      };
    },
    onSave() {
      if (this.id) {
        this.$apollo
          .mutate({
            mutation: UPDATE_OPEN_GROUP,
            variables: { id: this.id, input: this.buildInput() },
          })
          .then(() => {
            this.$store.dispatch("showAppSnackbar", {
              msg: "Open Group saved.",
            });
            this.$router.push("/open-groups");
          })
          .catch((err) => {
            this.$store.dispatch("showAppSnackbar", {
              msg: err.message,
              color: "error",
            });
          });
      } else {
        this.$apollo
          .mutate({
            mutation: CREATE_OPEN_GROUP,
            variables: { input: this.buildInput() },
          })
          .then(() => {
            this.$store.dispatch("showAppSnackbar", {
              msg: "Open Group created.",
            });
            this.$router.push("/open-groups");
          })
          .catch((err) => {
            this.$store.dispatch("showAppSnackbar", {
              msg: err.message,
              color: "error",
            });
          });
      }
    },
    loadOpenGroup(data) {
      const og = data.openGroup;
      if (!og) return;
      this.name = og.name || "";
      this.description = og.description || "";
      this.department = og.department || "";
      this.duration = og.duration || null;
      this.adults = og.adults || 0;
      this.children = og.children || 0;
      this.destination = og.destination || null;
      this.agent = og.agent || null;
      this.validFrom = og.validFrom ? og.validFrom.substring(0, 10) : null;
      this.validTo = og.validTo ? og.validTo.substring(0, 10) : null;
      this.departureDays = og.departureDays ? og.departureDays.split(",") : [];
    },
  },

  apollo: {
    openGroup: {
      query: OPEN_GROUP_QUERY,
      variables() {
        return { id: this.id };
      },
      skip() {
        return !this.id;
      },
      result({ data }) {
        this.loadOpenGroup(data);
      },
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/gt/open-groups/OpenGroupBuilder.vue